import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { requestRowBadge } from '../utils/requestKinds';
import {
  parseRowId as parseDnDId,
  rowIdFor,
  flattenVisibleRows,
  describeSelection,
} from '../utils/treeSelection';
import { moveEntriesToDestination } from '../utils/collectionBulkMove';
import { useTreeMultiSelect } from '../hooks/useTreeMultiSelect';
import { registerBulkDuplicate } from '../utils/sidebarBulkShortcuts';
import { useSidebarDrag } from '../hooks/useSidebarDrag';
import { renderSidebar } from './sidebar/renderSidebar';
import { sidebarContextMenu } from './sidebar/sidebarContextMenu';
import { useSidebarDragHandlers } from './sidebar/useSidebarDragHandlers';
import { useSidebarExpansion } from './sidebar/useSidebarExpansion';
import { useSidebarFileDrop } from './sidebar/useSidebarFileDrop';

// ─── Sidebar ──────────────────────────────────────────────────────────────────

const Sidebar = ({
  collections,
  onRefreshCollections,
  isAuthenticated = false,
  isLocalWorkspace = false,
  workspaceId, onCreateCollection, onImportCollection, onImportFileDrop, onExportCollection,
  onRunCollection, onCreateRequest, onCreateFolder, onDeleteFolder, onSelectRequest,
  onSelectCollection, onSelectFolder, onDeleteRequest, onDeleteCollection, onBulkDelete,
  onBulkDuplicate, onCreateFolderReturningId, onDuplicate, selectedRequest, selectedCollection,
  selectedFolder, editingItem, onStartRename, onSaveRename, onRenameDraft, onCancelRename,
  onCollectionsReorder, onShareCollection, onGenerateCode, onRevealInExplorer,
  // ── Schemas (embedded section) ──
  schemas, activeSchemaId, onOpenSchema, onCreateSchema, onCreateSchemaFolder, onRenameSchemaEntry,
  onDeleteSchemaEntry, onDuplicateSchema, onMoveSchemaEntry, editingSchemaItem, onStartSchemaRename,
  onSaveSchemaRename, onCancelSchemaRename, selectedSchemaEntry, onSelectSchemaEntry, fadeInIds,
}) => {
  const { t } = useTranslation();
  const collectionsCollapsedKey = workspaceId
    ? `sidebar-collections-section:${workspaceId}`
    : null;
  const [collectionsCollapsed, setCollectionsCollapsed] = useState(false);
  const [expandedCollections, setExpandedCollections] = useState(new Set());
  const [expandedFolders,     setExpandedFolders]     = useState(new Set());
  const expandedForWorkspaceRef = useRef(null);
  const prevCollectionIdsRef    = useRef(null);
  const preSearchStateRef       = useRef(null);

  const [contextMenu, setContextMenu] = useState(null);
  const [bgMenu,      setBgMenu]      = useState(null);
  // Which nested flyout of the context menu is open ('share' | null).
  const [openSubmenu, setOpenSubmenu] = useState(null);
  // { type, item }  — one row, today's path
  // { kind: 'bulk' } — the marked set, resolved at confirm time
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [movePickerOpen, setMovePickerOpen] = useState(false);
  const [searchTerm,  setSearchTerm]  = useState('');

  const [activeDragId,  setActiveDragId]  = useState(null);
  const [dropIndicator, setDropIndicator] = useState(null);

  const {
    fileDragActive, handleFileDragEnter, handleFileDragOver, handleFileDragLeave, handleFileDrop,
  } = useSidebarFileDrop({ onImportFileDrop });

  const { toggleCollectionsSection, filteredCollections } = useSidebarExpansion({
    collections, workspaceId, editingItem, collectionsCollapsedKey, setCollectionsCollapsed,
    expandedCollections, setExpandedCollections, expandedFolders, setExpandedFolders,
    expandedForWorkspaceRef, prevCollectionIdsRef, preSearchStateRef, searchTerm,
  });

  const {
    handleDragStart, handleDragMove, markedIdsRef, handleDragEnd, handleDragCancel,
  } = useSidebarDragHandlers({
    collections, onCollectionsReorder, setActiveDragId, setDropIndicator,
  });

  const displayCollections = searchTerm ? filteredCollections : collections;
  const dndDisabled        = !!searchTerm;
  const renderCollections  = displayCollections;

  // ── Multi-selection ────────────────────────────────────────────────────────
  // `markedIds` is NEW state, parallel to selectedRequest/selectedCollection/
  // selectedFolder. Those three mean "open"; this one means "marked". Merging
  // them would route a ctrl-click into the async, HTTP-fetching open path.

  // The shift-range runs over the rows that are ON SCREEN — the SAME collections
  // and the SAME expansion predicates the tree below renders with, so "visible"
  // here is the DOM's "visible" by construction.
  const visibleRows = useMemo(
    () => flattenVisibleRows(renderCollections, { expandedCollections, expandedFolders }),
    [renderCollections, expandedCollections, expandedFolders]
  );

  const { markedIds, handleRowClick, markOnly, clear: clearMarked } = useTreeMultiSelect({
    visibleRows,
    // Hand Escape back while one of the selection's OWN modals is open, or the
    // confirm dialog for a bulk delete could never be dismissed with the keyboard.
    // …and NOT while a drag is in flight: the drag owns Escape then, and it
    // listens on window BUBBLE, so a capture-phase consumer here would make an
    // aborted drag apply anyway on mouse-up — a bulk cross-collection move with
    // no undo. Found in the pre-release review of #171.
    escapeEnabled: !deleteConfirm && !movePickerOpen && !activeDragId,
  });
  markedIdsRef.current = markedIds;

  const selection = useMemo(
    () => describeSelection(markedIds, collections),
    [markedIds, collections]
  );
  const hasMultiSelection = markedIds.size > 1;

  // A workspace switch replaces the whole tree; carrying marks across it would
  // leave ids pointing at rows that no longer exist.
  useEffect(() => { clearMarked(); }, [workspaceId, clearMarked]);

  // ── Custom DnD hook ────────────────────────────────────────────────────────

  const { dragX, dragY, overlayRef, getHandleProps } = useSidebarDrag({
    disabled: dndDisabled || !onCollectionsReorder,
    onDragStart: handleDragStart,
    onDragMove:  handleDragMove,
    onDragEnd:   handleDragEnd,
    onDragCancel: handleDragCancel,
  });

  // ── Toggle helpers ─────────────────────────────────────────────────────────

  // Both use the functional setState form (rather than closing over expandedCollections)
  // so the callback identity stays stable across renders not caused by toggling itself —
  // required for CollectionBlock/FolderRow's React.memo to actually take effect.
  const toggleCollection = useCallback((collectionId) => {
    setExpandedCollections((prev) => {
      const wasExpanded = prev.has(collectionId);
      const next = new Set(prev);
      if (wasExpanded) {
        next.delete(collectionId);
      } else {
        next.add(collectionId);
        const col = collections.find((c) => String(c.id) === String(collectionId));
        if (col?.settings?.folders?.length) {
          setExpandedFolders((prevFolders) => {
            const nextFolders = new Set(prevFolders);
            for (const f of col.settings.folders) nextFolders.add(f.id);
            return nextFolders;
          });
        }
      }
      return next;
    });
  }, [collections]);

  const toggleFolder = useCallback((folderId) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId); else next.add(folderId);
      return next;
    });
  }, []);

  // ── Context menus ──────────────────────────────────────────────────────────

  const menuOpenedAtRef = useRef(0);
  const contextMenuRef = useRef(null);

  const handleContextMenu = useCallback((e, type, item) => {
    e.preventDefault();
    e.stopPropagation();
    menuOpenedAtRef.current = Date.now();
    setBgMenu(null);
    setOpenSubmenu(null);

    const rowId =
      item && (type === 'collection' || type === 'folder' || type === 'request')
        ? rowIdFor(type, item.id)
        : null;

    // Right-clicking INSIDE a multi-selection acts on the whole set. Right-
    // clicking OUTSIDE it first collapses the set onto that row — standard
    // file-manager behaviour, and the reason a menu can never quietly act on
    // rows the user has scrolled away from.
    if (rowId && markedIds.size > 1 && markedIds.has(rowId)) {
      setContextMenu({ x: e.clientX, y: e.clientY, type: 'selection', item: null });
      return;
    }
    if (rowId) markOnly(rowId);
    setContextMenu({ x: e.clientX, y: e.clientY, type, item });
  }, [markedIds, markOnly]);
  const closeContextMenu = () => {
    setOpenSubmenu(null);
    setContextMenu(null);
  };

  useEffect(() => {
    if (!contextMenu) return;
    const handleMouseDown = (e) => {
      if (Date.now() - menuOpenedAtRef.current < 250) return;
      if (contextMenuRef.current && contextMenuRef.current.contains(e.target)) return;
      closeContextMenu();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [contextMenu]);

  const handleBgContextMenu = (e) => {
    e.preventDefault();
    menuOpenedAtRef.current = Date.now();
    setContextMenu(null);
    setBgMenu({ x: e.clientX, y: e.clientY });
  };
  const closeBgMenu = () => setBgMenu(null);
  const handleBgOverlayDismiss = () => {
    if (Date.now() - menuOpenedAtRef.current < 250) return;
    closeBgMenu();
  };

  const clearSearch = () => setSearchTerm('');

  // ── Drag overlay label ─────────────────────────────────────────────────────

  const overlayCollections = collections;

  const overlayLabel = useMemo(() => {
    if (!activeDragId) return null;
    const p = parseDnDId(activeDragId);
    if (!p) return null;
    if (p.kind === 'collection') {
      const c = overlayCollections.find((x) => String(x.id) === String(p.key));
      return c ? { type: 'collection', name: c.name } : null;
    }
    if (p.kind === 'folder') {
      for (const c of overlayCollections) {
        const f = (c.settings?.folders || []).find((x) => String(x.id) === String(p.key));
        if (f) return { type: 'folder', name: f.name };
      }
      return null;
    }
    for (const c of overlayCollections) {
      const r = (c.requests || []).find((x) => String(x.id) === String(p.key));
      if (r) return { type: 'request', badge: requestRowBadge(r), name: r.name };
    }
    return null;
  }, [activeDragId, overlayCollections]);

  // ── Bulk actions on the marked set ─────────────────────────────────────────

  // `selection.items` is ALREADY normalised — a node whose ancestor is also
  // marked has been dropped. Deleting {folder-7, request-42∈7} therefore issues
  // one folder delete (which removes 42 with its subtree) and not a second,
  // 404-ing delete for a request the server no longer has.
  const handleBulkDeleteConfirm = useCallback(() => {
    if (!selection.canDelete) return;
    onBulkDelete?.(selection.items);
    clearMarked();
  }, [selection, onBulkDelete, clearMarked]);

  // Same normalised list as delete: duplicating {folder-7, request-42∈7} copies
  // the folder once and 42 rides along inside it, rather than ALSO getting a
  // second copy beside the original.
  const handleBulkDuplicate = useCallback(() => {
    if (!selection.canDuplicate) return;
    onBulkDuplicate?.(selection.items);
    clearMarked();
  }, [selection, onBulkDuplicate, clearMarked]);

  // Ctrl/Cmd+D is handled on `document` before this tree ever sees the key, and
  // that handler only knows the ONE selected row — see sidebarBulkShortcuts.
  // Registered once; the ref carries the latest selection.
  const bulkDuplicateShortcutRef = useRef(null);
  bulkDuplicateShortcutRef.current = () => {
    if (markedIds.size <= 1 || editingItem) return false;
    handleBulkDuplicate();
    // Handled even when the selection is read-only: with eight rows marked,
    // falling through to "duplicate the one selected row" is not what was asked.
    return true;
  };
  useEffect(() => registerBulkDuplicate(() => bulkDuplicateShortcutRef.current?.()), []);

  // Move reuses the drag path's commit point (`onCollectionsReorder`) rather
  // than inventing a second one: same setCollections, same tab re-parenting,
  // same debounced tree save. Like a drag, it emits no toast.
  const handleBulkMove = useCallback(
    (collectionId, folderId) => {
      setMovePickerOpen(false);
      if (!selection.canMove) return;
      const next = moveEntriesToDestination(collections, selection.ids, {
        collectionId,
        folderId: folderId ?? null,
      });
      if (next) onCollectionsReorder?.(next);
      clearMarked();
    },
    [collections, selection, onCollectionsReorder, clearMarked]
  );

  // Delete/Backspace with a multi-selection, taken in the CAPTURE phase so the
  // per-row handler underneath never also opens its single-item confirm. With
  // one or zero rows marked this does nothing at all and today's path runs
  // unchanged.
  const handleTreeKeyDownCapture = useCallback((e) => {
    if (e.key !== 'Delete' && e.key !== 'Backspace') return;
    if (markedIds.size <= 1) return;
    if (editingItem) return;
    e.preventDefault();
    e.stopPropagation();
    setDeleteConfirm({ kind: 'bulk' });
  }, [markedIds, editingItem]);

  const { renderContextMenu } = sidebarContextMenu({
    t, collections, isLocalWorkspace, onCreateCollection, onImportCollection, onExportCollection,
    onRunCollection, onCreateRequest, onCreateFolder, onDuplicate, onStartRename, onShareCollection,
    onGenerateCode, onRevealInExplorer, setContextMenu, openSubmenu, setOpenSubmenu,
    setDeleteConfirm, setMovePickerOpen, selection, contextMenuRef, closeContextMenu,
    handleBulkDuplicate,
  });

  return renderSidebar({
    t, collections, onRefreshCollections, isLocalWorkspace, workspaceId, onCreateCollection,
    onCreateRequest, onCreateFolder, onDeleteFolder, onSelectRequest, onSelectCollection,
    onSelectFolder, onDeleteRequest, onDeleteCollection, onCreateFolderReturningId, selectedRequest,
    selectedCollection, selectedFolder, editingItem, onSaveRename, onRenameDraft, onCancelRename,
    schemas, activeSchemaId, onOpenSchema, onCreateSchema, onCreateSchemaFolder,
    onRenameSchemaEntry, onDeleteSchemaEntry, onDuplicateSchema, onMoveSchemaEntry,
    editingSchemaItem, onStartSchemaRename, onSaveSchemaRename, onCancelSchemaRename,
    selectedSchemaEntry, onSelectSchemaEntry, fadeInIds, collectionsCollapsed, expandedCollections,
    expandedFolders, contextMenu, bgMenu, deleteConfirm, setDeleteConfirm, movePickerOpen,
    setMovePickerOpen, searchTerm, setSearchTerm, activeDragId, dropIndicator, fileDragActive,
    handleFileDragEnter, handleFileDragOver, handleFileDragLeave, handleFileDrop, toggleCollection,
    toggleCollectionsSection, filteredCollections, markedIds, dndDisabled, renderCollections,
    selection, handleRowClick, hasMultiSelection, dragX, dragY, overlayRef, getHandleProps,
    toggleFolder, handleContextMenu, handleBgContextMenu, closeBgMenu, handleBgOverlayDismiss,
    clearSearch, overlayLabel, handleBulkDeleteConfirm, handleBulkMove, handleTreeKeyDownCapture,
    renderContextMenu,
  });
};

export default Sidebar;
